import React, { useEffect, useState, useRef } from "react";
import type { TableProps } from "antd";
import { ColumnsType, ColumnType } from "antd/es/table";
import {
  Form,
  Input,
  Upload,
  Popconfirm,
  Table,
  Typography as Typographys,
  Button as Buttons,
  Select,
  notification,
  Space,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import DocumentsController from "./Controller/document_controller.ts";
import Link from "@mui/material/Link";
import CustomSeparator from "./../../layouts/AdminLayout/Header/Breadcrumbs.js";
import Typography from "@mui/material/Typography";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { createStyles } from "antd-style";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
} from "@mui/material";
import placeholder from "../../images/placeholder.jpg";

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

const pdf_png = require("./../../images/pdf.png");
type NotificationType = "success" | "info" | "warning" | "error";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  key: string;
  userID: string;
  documentID: string;
  document_label: string;
  document_poster: string;
  document_author_image: string;
  author_name: string;
  description: string;
  datetime_created: string;
  datetime_expired: string;
  datetime_updated: string;
  document_categoryID: string;
  document_category_label: string;
  file_url: string;
  updater_userID: string;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "document" | "document_categoryID" | "poster";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  let inputNode;

  if (inputType === "document") {
    inputNode = (
      <Upload beforeUpload={() => false} listType="picture">
        <Buttons color="default" variant="solid" icon={<UploadOutlined />}>
          Choose...
        </Buttons>
      </Upload>
    );
  } else if (inputType === "document_categoryID") {
    inputNode = (
      <Select
        value={
          record.document_categoryID == null
            ? "null"
            : record.document_categoryID
        }
      >
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );
  } else if (inputType === "poster") {
    // File upload input
    inputNode = (
      <Upload beforeUpload={() => false} listType="picture">
        <Buttons color="default" variant="solid" icon={<UploadOutlined />}>
          Choose...
        </Buttons>
      </Upload>
    );
  } else {
    inputNode = <Input />;
  }

  // File handler function
  const getFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList; // Return file list
  };

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[{ required: true, message: `Please input ${title}!` }]}
          valuePropName={
            ["poster", "document"].includes(inputType) ? dataIndex : "value"
          } // Check for both conditions
          getValueFromEvent={
            ["poster", "document"].includes(inputType) ? getFile : undefined
          } // Use getFile if it's a file input
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const userID = localStorage.getItem("userID");

const CheckboxList = ({ columns, onChange, checkedColumns }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // State for checkboxes, synchronized with `checkedColumns` from parent
  const [checked, setChecked] = useState(checkedColumns);

  useEffect(() => {
    setChecked(checkedColumns); // Synchronize state with prop
  }, [checkedColumns]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    onChange(newChecked); // Notify parent about changes
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {columns.map((value) => {
            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checked.includes(value.dataIndex)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString); // Convert to Date object
  return date.toLocaleString("en-US", {
    weekday: "long", // Example: Friday
    year: "numeric", // Example: 2024
    month: "long", // Example: November
    day: "numeric", // Example: 29
    hour: "numeric", // Example: 9
    minute: "numeric", // Example: 30
  });
};
const App: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (
    type: NotificationType,
    desc: string,
    message: String
  ) => {
    api[type]({
      message: message,
      description: desc,
    });
  };

  const [form] = Form.useForm();
  const [rows, setRows] = useState<any[]>([]);
  const [documentCategoryFilter, setDocumentCategoryFilters] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    DocumentsController.getDocuments().then((response) => {
      console.log(response);
      const rowData = response.map((item, index) => ({
        key: index,
        documentID: item.documentID,
        document_label: item.label,
        description: item.description,
        datetime_created: item.datetime_created.split(" ")[0],
        datetime_expired: item.datetime_expired,
        datetime_updated: item.datetime_updated,
        document_categoryID: item.document_categoryID,
        document_category_label: item.document_category_label,
        file_url: item.file_url,
        document_poster: item.poster_url,
        document_author_image: item.author_image_url,
        author_name: item.author_name,
        updater_userID: item.updater_userID,
      }));

      setRows(Array.from(rowData));
    });

    DocumentsController.getResourcesCategories().then((response) => {
      // Transform the API response to match the filters pattern
      const filters = response.map((item) => ({
        text: item.label, // Use the appropriate key for the display text
        value: item.document_categoryID, // Use the appropriate key for the filter value
      }));

      setDocumentCategoryFilters(filters);
    });
  }, []);

  // Update data whenever rows changes
  useEffect(() => {
    setRows(rows);
  }, [rows]);

  type ApiResponse = {
    data: any[];
    status: string;
  };
  const refreshData = ({ status, data }: ApiResponse) => {
    const rows = data.map((item: any, index: number) => ({
      key: index,
      documentID: item.documentID,
      document_label: item.label,
      description: item.description,
      datetime_created: item.datetime_created.split(" ")[0],
      datetime_expired: item.datetime_expired,
      datetime_updated: item.datetime_updated,
      document_categoryID: item.document_categoryID,
      document_category_label: item.document_category_label,
      file_url: item.file_url,
      document_poster: item.poster_url,
      document_author_image: item.author_image_url,
      author_name: item.author_name,
      updater_userID: item.updater_userID,
    }));

    setRows(rows);
  };
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => record.key === editingKey;

  const edit = (record: Partial<DataType> & { key: React.Key }) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const delete_record = (rowId: string, userID) => {
    const payload = new FormData();
    payload.append("documentID", rowId); // Append event ID
    payload.append("userID", userID); // Append event ID
    DocumentsController.deleteDocuments(payload).then((response) => {
      console.log(response);
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been deleted successfully!",
          "Success"
        );

        refreshData(response);
      }
    });
  };

  const save = async (key: React.Key, userID) => {
    try {
      const row = (await form.validateFields()) as DataType;

      const newData = [...rows];
      const index = newData.findIndex((item) => {
        return key === item.documentID;
      });
      if (index > -1) {
        const item = newData[index];
        const documentID = item.documentID;

        const payload = new FormData();
        payload.append("documentID", documentID); // Append event ID
        payload.append("userID", userID); // Append event ID

        // Append form data fields (except the file)
        Object.keys(row).forEach((field) => {
          if (field !== "file_url") {
            // Skip the file field
            payload.append(`data[${field}]`, row[field]); // Add form fields to payload
          }
        });

        // Append the file (if uploaded)
        if (row.file_url && row.file_url.length > 0) {
          const file = row.file_url[0]["originFileObj"]; // Get file object
          payload.append("document", file); // Add the file to the payload

          // console.log(file);
        }

        // Append form data fields (except the file)
        Object.keys(row).forEach((field) => {
          if (field !== "document_poster") {
            // Skip the file field
            payload.append(`data[${field}]`, row[field]); // Add form fields to payload
          }
        });

        // Append the file (if uploaded)
        if (row.document_poster && row.document_poster.length > 0) {
          const poster = row.document_poster[0]["originFileObj"]; // Get file object
          payload.append("poster", poster); // Add the file to the payload

          // console.log(poster);
        }

        // Append form data fields (except the file)
        Object.keys(row).forEach((field) => {
          if (field !== "document_author_image") {
            // Skip the file field
            payload.append(`data[${field}]`, row[field]); // Add form fields to payload
          }
        });

        // Append the file (if uploaded)
        if (row.document_author_image && row.document_author_image.length > 0) {
          const author_image = row.document_author_image[0]["originFileObj"]; // Get file object
          payload.append("author_image", author_image); // Add the file to the payload

          // console.log(author_image);
        }

        DocumentsController.editDocuments(payload).then((response) => {
          console.log(response);

          if (response?.status === "success") {
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!",
              "Success"
            );
            // Handle successful response and update rows or UI
            refreshData(response);
          }
        });

        setRows(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setRows(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const ConvertUrlToFinalUrl = (url: string) => {
    let res = url;
    if (!res.includes("www.") && !res.includes("localhost")) {
      res = res.replace("http://", "http://www.");
      res = res.replace("https://", "https://www.");
    }
    return res;
  };

  const handleDownload = (data) => {
    // Ensure `data` is a valid URL
    const fileName = data.split("/").pop() || "document.pdf";

    // console.log(fileName);

    // Use `fetch` to download the file
    fetch(data)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch the file.");
        }
        return response.blob(); // Convert the response to a Blob
      })
      .then((blob) => {
        // Create an object URL from the Blob
        const fileURL = window.URL.createObjectURL(blob);
        // console.log(fileURL);

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = ConvertUrlToFinalUrl(fileURL);
        // link.rel = "noopener noreferrer";
        link.download = fileName; // Set the file name for download

        // console.log(ConvertUrlToFinalUrl(fileURL));

        // Append to the DOM and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileURL); // Free up memory
      })
      .catch((error) => {
        console.error("Error downloading the file:", error);
      });
  };
  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const columns = [
    {
      title: "Document",
      dataIndex: "file_url",
      editable: true,
      render: (data: string, record: DataType) => {
        const fileName = ConvertUrlToFinalUrl(data).split("/").pop();
        const fileExtension = fileName?.split(".").pop(); // Extracts the extension
        // console.log(data);

        return (
          <>
            {fileExtension === "pdf" ? (
              <img
                src={pdf_png}
                alt="PDF Icon"
                style={{ width: "40px", marginRight: "8px" }}
              />
            ) : (
              <div></div>
            )}

            <Link
              onClick={(e) => {
                e.preventDefault(); // Prevent default anchor behavior
                handleDownload(ConvertUrlToFinalUrl(data));
              }}
              underline="none"
            >
              {record.document_label}
            </Link>
          </>
        );
      },
    },
    {
      title: "Poster",
      dataIndex: "document_poster",
      key: "document_poster",
      editable: true,
      render: (text: any) => {
        return (
          <>
            {text && text.trim() ? (
              <img
                src={text}
                alt="Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            ) : (
              <img
                src={placeholder}
                alt="Poster"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            )}
          </>
        );
      },
    },
    {
      title: "Author Image",
      dataIndex: "document_author_image",
      key: "document_author_image",
      editable: true,
      render: (text: any) => {
        return (
          <>
            {text && text.trim() ? (
              <img
                src={text}
                alt="Author Image"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            ) : (
              <img
                src={placeholder}
                alt="Author Image"
                style={{ width: "100px", height: "auto", objectFit: "cover" }} // Set styles as needed
              />
            )}
          </>
        );
      },
    },
    {
      title: "Author Name",
      dataIndex: "author_name",
      width: "15%",
      editable: true,
      ...getColumnSearchProps("author_name"), //IMPLEMENT SEARCH FILTER
    },
    {
      title: "Label",
      dataIndex: "document_label",
      width: "15%",
      editable: true,
      ...getColumnSearchProps("document_label"), //IMPLEMENT SEARCH FILTER
    },
    {
      title: "Description",
      dataIndex: "description",
      editable: true,
      ...getColumnSearchProps("description"),
    },
    {
      title: "Date Created",
      dataIndex: "datetime_created",
      editable: true,
      // render: (data: any) => {
      //   const readableDate = formatDate(data);
      //   return <div>{readableDate}</div>;
      // },
    },
    {
      title: "Date Time Expired",
      dataIndex: "datetime_expired",
      editable: true,
    },
    // {
    //   title: "Date Time Updated",
    //   dataIndex: "datetime_updated",
    // },
    {
      title: "Type",
      dataIndex: "document_categoryID",
      editable: true,
      render: (data: any, record: DataType) => {
        return <Typographys>{record.document_category_label}</Typographys>;
      },
      filters: documentCategoryFilter,
      onFilter: (value, record) => record.document_categoryID === value,
    },
    // {
    //   title: "Updated By",
    //   dataIndex: "updater_userID",
    // },
    {
      title: "operation",
      dataIndex: "operation",
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return editable ? (
          <>
            <Buttons
              onClick={() => save(record.documentID, userID)}
              color="default"
              variant="solid"
              style={{ marginBottom: "10px" }}
            >
              Save
            </Buttons>
            <Buttons color="default" variant="outlined">
              <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                Cancel
              </Popconfirm>
            </Buttons>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "90%",
            }}
          >
            <Buttons
              color="default"
              variant="solid"
              icon={<EditOutlined />}
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              style={{ marginBottom: "10px" }}
            >
              Edit
            </Buttons>
            <Buttons
              type="primary"
              danger
              icon={<DeleteOutlined />}
              disabled={editingKey !== "" || editingKey !== ""}
            >
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => delete_record(record.documentID, userID)}
              >
                Delete
              </Popconfirm>
            </Buttons>
          </div>
        );
      },
    },
  ];

  const [documentCategoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    DocumentsController.getResourcesCategories().then((response) => {
      const options = response.map((item) => ({
        value: item.document_categoryID,
        label: item.label,
      }));

      // Add a new row at the end of the array
      options.push({
        label: "None", // Customize as needed
        value: "null", // Provide the appropriate value
      });
      setCategoryOptions(options);
    });
  }, []);

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);

    // call api here
  };

  // const rowSelection: TableRowSelection<DataType> = {
  //   selectedRowKeys,
  //   onChange: onSelectChange,
  // };

  const [checkedColumns, setCheckedColumns] = useState([
    "document_categoryID",
    "file_url",
    "document_label",
    "description",
    "datetime_created",
    "datetime_expired",
    // "datetime_updated",
    // "updater_userID",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  const mergedColumns = columns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => ({
          record,
          inputType:
            col.dataIndex === "file_url"
              ? "document"
              : col.dataIndex === "document_categoryID"
              ? "document_categoryID"
              : col.dataIndex === "document_poster"
              ? "poster"
              : col.dataIndex === "document_author_image"
              ? "poster"
              : "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          options:
            col.dataIndex === "document_categoryID"
              ? documentCategoryOptions
              : undefined,
        }),
      };
    });

  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "100%", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />
          <CustomSeparator pageName="resources" />
          {contextHolder}

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Resources Directory
          </Typography>
          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList
              columns={columns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                className={styles.customTable}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                dataSource={rows}
                columns={mergedColumns as ColumnType<DataType>[]}
                rowClassName="editable-row"
                pagination={{ onChange: cancel }}
                scroll={{ x: "80vh", y: 150 * 5 }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default App;
